// ============================================================
// FINART STUDIO — Helpers: limiti, formatiranje, datumi
// ============================================================

export const LIMIT_PAUSAL = 6000000;   // sve fakture (dom + ino), kalendarska godina
export const LIMIT_PDV    = 8000000;   // samo domaće fakture, rolling 365 dana

export const CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF', 'HUF', 'CAD', 'AUD'];

export const ACTIVITY_CODES = [
  { code: '6201', name: 'Računarsko programiranje' },
  { code: '6202', name: 'IT konsultantske delatnosti' },
  { code: '6209', name: 'Ostale IT usluge' },
  { code: '6311', name: 'Obrada podataka i hosting' },
  { code: '6312', name: 'Veb portali' },
  { code: '6399', name: 'Ostale informacione usluge' },
  { code: '6910', name: 'Pravni poslovi' },
  { code: '6920', name: 'Računovodstveni i knjigovodstveni poslovi' },
  { code: '7021', name: 'Komunikacije i odnosi s javnošću' },
  { code: '7022', name: 'Poslovno i upravljačko savetovanje' },
  { code: '7410', name: 'Specijalizovane dizajnerske aktivnosti' },
  { code: '7420', name: 'Fotografske usluge' },
  { code: '8211', name: 'Kombinovane kancelarijsko-administrativne usluge' },
  { code: '8219', name: 'Pripremanje dokumenata i kancelarijska podrška' },
  { code: '8299', name: 'Ostale pomoćne poslovne uslužne delatnosti' },
  { code: '8559', name: 'Ostalo obrazovanje' },
  { code: '9001', name: 'Izvođačka umetnost' },
  { code: '9002', name: 'Pomoćne umetničke delatnosti' },
  { code: '9003', name: 'Umetničko stvaralaštvo' },
  { code: '9602', name: 'Delatnost frizerskih i kozmetičkih salona' },
  { code: '9609', name: 'Ostale nepomenute lične uslužne delatnosti' },
];

export const formatRSD = (n) =>
  new Intl.NumberFormat('sr-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0) + ' RSD';

export const formatCur = (n, cur) =>
  new Intl.NumberFormat('sr-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0) + ' ' + cur;

export const formatPct = (n) =>
  new Intl.NumberFormat('sr-RS', { maximumFractionDigits: 0 }).format(n || 0) + '%';

export const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('sr-RS') : '';

export const fmtMonthShort = (year, month) => {
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Avg', 'Sep', 'Okt', 'Nov', 'Dec'];
  return `${names[month]} ${year}`;
};

export const todayStr = () => new Date().toISOString().split('T')[0];

export function toRSD(inv) {
  if (inv.document_kind === 'profaktura') return 0; // profaktura se ne računa u limite
  if (inv.status === 'stornirano') return 0;          // stornirana faktura se ne računa u promet
  return inv.type === 'domaca' || inv.document_kind === 'fiskalni'
    ? inv.amount
    : inv.amount * (inv.exchange_rate || 1);
}

// Limit 6M paušal — SVE fakture (domaće + inostrane + fiskalni), kalendarska godina
export function pausaCalendarTotal(invoices) {
  const y = new Date().getFullYear();
  return invoices
    .filter(i => i.document_kind !== 'profaktura' && new Date(i.issue_date).getFullYear() === y)
    .reduce((s, i) => s + toRSD(i), 0);
}

// Limit 8M PDV — SAMO domaće fakture i fiskalni, poslednjih 365 dana
export function pdvRollingTotal(invoices) {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 365);
  return invoices
    .filter(i => (i.type === 'domaca' || i.document_kind === 'fiskalni')
      && i.document_kind !== 'profaktura'
      && new Date(i.issue_date) >= cutoff)
    .reduce((s, i) => s + i.amount, 0);
}

export function totalSaldo(invoices) {
  return invoices
    .filter(i => i.document_kind !== 'profaktura')
    .reduce((s, i) => s + toRSD(i), 0);
}

export function groupByCurrency(invoices) {
  const groups = {};
  invoices.filter(i => i.type === 'inostrana' && i.document_kind !== 'profaktura').forEach(i => {
    groups[i.currency] = (groups[i.currency] || 0) + i.amount;
  });
  return groups;
}

// Fakturisano po mesecima za dati broj meseci unazad (za grafikon)
export function monthlyTotals(invoices, monthsBack = 6) {
  const now = new Date();
  const buckets = [];
  for (let i = monthsBack - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    buckets.push({ year: d.getFullYear(), month: d.getMonth(), total: 0 });
  }
  invoices
    .filter(inv => inv.document_kind !== 'profaktura')
    .forEach(inv => {
      const d = new Date(inv.issue_date);
      const bucket = buckets.find(b => b.year === d.getFullYear() && b.month === d.getMonth());
      if (bucket) bucket.total += toRSD(inv);
    });
  return buckets;
}

// Top klijenti (kupci) po fakturisanom iznosu
export function topCustomers(invoices, limit = 4) {
  const totals = {};
  invoices
    .filter(inv => inv.document_kind !== 'profaktura' && inv.document_kind !== 'pazar')
    .forEach(inv => {
      const key = inv.customer?.name || inv.client || 'Nepoznat kupac';
      const isForeign = inv.type === 'inostrana';
      if (!totals[key]) totals[key] = { name: key, total: 0, isForeign };
      totals[key].total += toRSD(inv);
    });
  const sum = Object.values(totals).reduce((s, t) => s + t.total, 0) || 1;
  return Object.values(totals)
    .sort((a, b) => b.total - a.total)
    .slice(0, limit)
    .map(t => ({ ...t, pct: Math.round((t.total / sum) * 100) }));
}

// Sledeći poreski rok — paušalci plaćaju do 15. za prethodni mesec
export function nextTaxDeadline() {
  const now = new Date();
  let year = now.getFullYear();
  let month = now.getMonth(); // 0-indexed, this = current month
  let deadline = new Date(year, month, 15);
  if (now > deadline) {
    deadline = new Date(year, month + 1, 15);
  }
  return deadline;
}
